import { useEffect, useRef } from 'react';
import { CaretRight, X } from '@phosphor-icons/react';
import './AndysSiteMenu.css';

const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * AndysSiteMenu — панель навигации по сайту дилера для лендинга Andys (#1469):
 * лендинг стал мобильной главной, и с него нужно попасть на остальные страницы.
 *
 * Шторка сверху + скрим под ней. Кнопку-триггер (☰ в шапке героя) и состояние
 * держит родитель; панель всегда в DOM (`hidden`, пока закрыта), чтобы
 * `aria-controls` триггера ссылался на существующий элемент.
 *
 * Закрывается: крестиком, Esc, тапом по скриму, выбором пункта.
 *
 * Esc закрывает ТОЛЬКО меню. Overlay лендинга (`AndysLandingOverlay`) слушает
 * Esc на `window` и по нему закрывает весь лендинг. Поэтому слушатель меню
 * висит на `window` в фазе ПЕРЕХВАТА (срабатывает раньше всплытия, где ждёт
 * overlay, и при любом фокусе — даже если тот ушёл на body) и помечает событие
 * `preventDefault()`; overlay обработанный Esc пропускает.
 *
 * Фокус: при открытии — на первый интерактивный элемент панели, Tab/Shift+Tab
 * зациклены внутри неё; при закрытии родитель возвращает фокус на триггер
 * (кроме ухода по ссылке — там страница всё равно меняется).
 *
 * @param {object} props
 * @param {string} props.id — id панели (на него ссылается `aria-controls`).
 * @param {boolean} props.open
 * @param {Array<{id: string, label: string, href: string}>} props.items
 * @param {(opts: {restoreFocus: boolean}) => void} props.onClose
 * @param {(item: {id: string, label: string, href: string}) => void} [props.onSelect]
 *   — выбран пункт, до перехода по ссылке (аналитика, #1482).
 */
export const AndysSiteMenu = ({ id, open, items, onClose, onSelect }) => {
  const panelRef = useRef(null);
  const titleId = `${id}-title`;

  useEffect(() => {
    if (open) panelRef.current?.querySelector(FOCUSABLE)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose({ restoreFocus: true });
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      const nodes = panel ? Array.from(panel.querySelectorAll(FOCUSABLE)) : [];
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      // getRootNode: внутри Shadow DOM document.activeElement — это shadow host,
      // реальный фокус знает только сам shadow root.
      const active = panel.getRootNode().activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  return (
    <div className="andys-menu" hidden={!open}>
      {/* Скрим — «тап мимо меню». Не кнопка и вне a11y-дерева: для клавиатуры
          и скринридера есть крестик и Esc. */}
      <div
        className="andys-menu__scrim"
        aria-hidden="true"
        onClick={() => onClose({ restoreFocus: true })}
      />
      <div
        id={id}
        ref={panelRef}
        className="andys-menu__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="andys-menu__head">
          <span id={titleId} className="andys-menu__title">
            Menu
          </span>
          <button
            type="button"
            className="andys-menu__close"
            aria-label="Close menu"
            onClick={() => onClose({ restoreFocus: true })}
          >
            <X size={24} weight="regular" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Andy's Motors">
          <ul className="andys-menu__list">
            {items.map((item) => (
              <li key={item.id}>
                <a
                  className="andys-menu__link"
                  href={item.href}
                  onClick={() => {
                    onSelect?.(item);
                    onClose({ restoreFocus: false });
                  }}
                >
                  {item.label}
                  <CaretRight size={20} weight="regular" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default AndysSiteMenu;
